import { Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Divider } from '@mui/material';
import { Alert, Card, Chip, Select, SelectItem, Grid, Typography } from '@platform-ui/design-system';

import React, { Dispatch, useEffect, useState } from 'react';
import Connect from "../../../../Connect/Connect";
import { IADState } from '../../../../IntegrationApps/IntegrationAppDetails/state';
import { Action, useStoreContext } from '../../../../Store';
import { useToast } from '../../../../ToastProvider';
import { featuresConfig, FeatureType } from './featuresConfig';

/**
 * Window interface declaration. It extends the global Window interface to include the connect property.
 */
declare global {
  interface Window {
    connect: any;
  }
}

/**
 * Feature setting interface. It represents the settings stored in Daco.
 */
interface FeatureSetting {
  setting: string;
  value: string;
  tenantId: string;
  createdBy: string | null;
  updatedBy: string | null;
  createdOn: string;
  updatedOn: string;
}

/**
 * Feature controls props interface. It represents the props passed to the FeatureControls component.
 * @property {string} tabName - The name of the tab. Which can be uses in a the URL to navigate to the tab.
 */
interface FeatureControlsProps {
  tabName: string;
}

/**
 * Feature controls component. It is used to control the features of the CRM connector.
 * @param {FeatureControlsProps} props - The props passed to the component.
 */
function FeatureControls({ tabName }: FeatureControlsProps) {
  const { state, dispatch } = useStoreContext() as { state: IADState, dispatch: Dispatch<Action> };
  const { setToast } = useToast();
  
  // Features as react state
  const [featureSettings, setFeatureSettings] = useState<FeatureSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Daco base URL
  const dacoBasePath = `${window.location.host.includes('local') ? 'http://localhost:8080' : ''}/platform/gateway-proxy-v2/datacon`;

  /**
   * Wait for a property to be available on the window object.
   * @param {string} property - The property to wait for.
   * @param {() => void} callback - The callback to execute when the property is available.
   */
  const waitFor = (property: string, callback: () => void) => {
    if (window.hasOwnProperty(property)) {
      callback();
    } else {
      setTimeout(() => waitFor(property, callback), 100);
    }
  };

  /**
   * Fetch feature settings from Daco.
   * @param {any} headers - The headers to use for the request.
   * @returns {Promise<FeatureSetting[]>} - The feature settings.
   */
  const fetchFeatureSettings = async (headers: any): Promise<FeatureSetting[]> => {
    try {
      const response = await fetch(`${dacoBasePath}/settings/`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      Connect.log('Error fetching feature settings:', error);
      setToast({
        message: 'Failed to fetch feature settings',
        severity: 'error'
      });
      return [];
    }
  };

  /**
   * Save a feature setting to Daco.
   * @param {string} setting - The setting to save.
   * @param {string} value - The value of the setting.
   * @param {any} headers - The headers to use for the request.
   * @returns {Promise<boolean>} - Whether the save was successful.
   */
  const saveFeatureSetting = async (setting: string, value: string, headers: any): Promise<boolean> => {
    try {
      const response = await fetch(`${dacoBasePath}/settings/`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          setting,
          value
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      Connect.log('Error saving feature setting:', error);
      return false;
    }
  };

  /**
   * Handle a feature change.
   * @param {string} featureApiName - The API name of the feature.
   * @param {string} newValue - The new value of the feature.
   */
  const handleFeatureChange = async (featureApiName: string, newValue: string) => {
    setSaving(true);
    
    // Resolve tenant and user ID from cookies if available.
    const cookies: any[] = (window.connect.current_user as any).cookies || [];
    let zuoraTenantIdFromCookie = '';
    let zuoraUserIdFromCookie = '';
    
    cookies.forEach((cookie) => {
      if (cookie[0] === 'Zuora-Tenant-Id') {
        zuoraTenantIdFromCookie = cookie[1];
      }
      if (cookie[0] === 'Zuora-User-Id') {
        zuoraUserIdFromCookie = cookie[1];
      }
    });

    // The headers to use for the request.
    const headers = {
      'Content-Type': 'application/json',
      'Zuora-Tenant-Id': window.connect?.tenant?.tenant_id || zuoraTenantIdFromCookie,
      'Zuora-User-Id': zuoraUserIdFromCookie
    };

    // call DACO API to save the feature setting.
    const success = await saveFeatureSetting(featureApiName, newValue, headers);
    
    if (success) {
      // Update local state
      setFeatureSettings(prev => {
        const updated = [...prev];
        const existingIndex = updated.findIndex(setting => setting.setting === featureApiName);
        
        if (existingIndex >= 0) {
          updated[existingIndex] = {
            ...updated[existingIndex],
            value: newValue,
            updatedBy: userId,
            updatedOn: new Date().toISOString()
          };
        } else {
          updated.push({
            setting: featureApiName,
            value: newValue,
            tenantId: window.connect?.tenant?.tenant_id || zuoraTenantIdFromCookie,
            createdBy: userId,
            updatedBy: userId,
            createdOn: new Date().toISOString(),
            updatedOn: new Date().toISOString()
          });
        }
        
        return updated;
      });
      
      setToast({
        message: 'Feature setting updated successfully',
        severity: 'success'
      });
    } else {
      setToast({
        message: 'Failed to update feature setting',
        severity: 'error'
      });
    }
    
    setSaving(false);
  };

  /**
   * Get the value of a feature setting.
   * @param {string} apiName - The API name of the feature.
   * @returns {string} - The value of the feature setting.
   */
  const getFeatureValue = (apiName: string): string => {
    const setting = featureSettings.find(s => s.setting === apiName);
    return setting?.value || '';
  };

  /**
   * Filter the features based on the search term.
   * @param {string} searchTerm - The search term.
   * @returns {any[]} - The filtered features.
   */
  const filteredFeatures = featuresConfig.filter(feature => {
    const searchLower = searchTerm.toLowerCase();
    return feature.title.toLowerCase().includes(searchLower) ||
           feature.description.toLowerCase().includes(searchLower);
  });

  /**
   * Render a feature control.
   * @param {any} feature - The feature to render.
   * @returns {JSX.Element} - The rendered feature control.
   */
  const renderFeatureControl = (feature: any) => {
    const currentValue = getFeatureValue(feature.apiName);
    const isEditable = feature.editable !== false; // Default to true if not specified
    
    switch (feature.type) {
      case FeatureType.BOOLEAN:
        return (
          <Button
            variant="text"
            size="medium"
            onClick={() => handleFeatureChange(feature.apiName, currentValue === 'true' ? 'false' : 'true')}
            disabled={saving || !isEditable}
            sx={{ minWidth: 80 }}
          >
            {currentValue === 'true' ? 'Disable' : 'Enable'}
          </Button>
        );
      
      case FeatureType.DROPDOWN:
        return (
          <Select
            value={currentValue || feature.options?.[0]?.value || ''}
            dsOnChange={(event: any) => handleFeatureChange(feature.apiName, String(event.target.value))}
            disabled={saving || !isEditable}
            sx={{ minWidth: 150 }}
          >
            {feature.options?.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        );
      
      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {currentValue || 'Not set'}
          </Typography>
        );
    }
  };

  useEffect(() => {
    // Wait for connect to be available
    const fetchData = async () => {
      while (!window.hasOwnProperty('connect')) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      waitFor('connect', async function () {
        const cookies: any[] = (window.connect.current_user as any).cookies || [];

        // Get the tenant and user id from cookies
        let zuoraTenantIdFromCookie = '';
        let zuoraUserIdFromCookie = '';
        
        cookies.forEach((cookie) => {
          if (cookie[0] === 'Zuora-Tenant-Id') {
            zuoraTenantIdFromCookie = cookie[1];
          }
          if (cookie[0] === 'Zuora-User-Id') {
            zuoraUserIdFromCookie = cookie[1];
          }
        });
        
        // Set the user id
        setUserId(zuoraUserIdFromCookie);

        // Set the headers we will be using.
        const headers = {
          'Content-Type': 'application/json',
          'Zuora-Tenant-Id': window.connect?.tenant?.tenant_id || zuoraTenantIdFromCookie,
          'Zuora-User-Id': zuoraUserIdFromCookie
        };

        // Fetch the feature settings
        const settings = await fetchFeatureSettings(headers);
        setFeatureSettings(settings);
        setLoading(false);
      });
    };

    fetchData();
  }, []);

  // If the credentials have not been set, show a warning
  if (!state.credsModified) {
    return (
      <Alert center variant='outlined' severity='warning' body='You must set up credentials in Authentication tab first' open={true} />
    );
  }

  // If the data is still loading, show a loading spinner
  if (loading) {
    return (
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Render the feature controls
  return (
    <Card
      id='zdp-feature-controls'
      body={
        <Grid container direction='column' spacing={3}>
          {/* Title and Description */}
          <Grid item>
            <Grid container direction='column' spacing={0.5}>
              <Grid item>
                <Typography variant='h5'>Feature Management</Typography>
              </Grid>
              <Grid item>
                <Typography variant='body2'>
                  Configure advanced sync capabilities and data handling rules.
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Separator */}
          <Grid item>
            <Divider />
          </Grid>

          {/* Search Box */}
          <Grid item>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type here to find features..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ mb: 1 }}
            />
          </Grid>

          {/* Features Table */}
          <Grid item>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant="subtitle2">Feature Name</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2">Description</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2">Status</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2">Actions</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeatures.map((feature) => (
                    <TableRow key={feature.apiName}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {feature.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {feature.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {feature.type === FeatureType.BOOLEAN ? (
                          <Chip
                            label={getFeatureValue(feature.apiName) === 'true' ? 'Enabled' : 'Disabled'}
                            variant="outlined"
                            size="small"
                            state={getFeatureValue(feature.apiName) === 'true' ? 'success' : 'default'}
                          />
                        ) : (
                          <Typography variant="body2">
                            {getFeatureValue(feature.apiName) || 'Default'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {renderFeatureControl(feature)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      }
    />
  );
}

export default FeatureControls;
